/* eslint-disable max-classes-per-file */
import { ProductUpsellTypeEnum } from '@hotjar/upsell';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type { SelectedProductTypes } from '../MultiProductWrapper/hooks/useMultiProductsState';

export class ListNode<T> {
  public next: ListNode<T> | null = null;

  public prev: ListNode<T> | null = null;

  constructor(public data: T) {
    this.data = data;
  }
}

interface ILinkedList<T> {
  add(data: T): ListNode<T>;
}

/**
 * Simple doubly linked list. Each node holds some data and a pointer to its previous
 * or next node. Useful for back/forward actions in the PlanSelectModal
 */
export class LinkedList<T> implements ILinkedList<T> {
  public length: number;

  constructor() {
    this.length = 0;
  }

  public head: ListNode<T> | null = null;

  public add(data: T): ListNode<T> {
    const node = new ListNode(data);

    if (!this.head) {
      this.head = node;
    } else {
      const getLast = (listNode: ListNode<T>): ListNode<T> => {
        return listNode.next ? getLast(listNode.next) : listNode;
      };

      const lastNode = getLast(this.head);
      node.prev = lastNode;
      lastNode.next = node;
    }

    this.length += 1;
    return node;
  }

  // Get the number of steps to display in modal title and logic
  // We reduce step count by one since checkout step is
  // not summed up in the total step count
  public noOfSteps(): number {
    return this.length - 1;
  }
}

export type PlanSelectNextStep = 'ask' | 'observe' | 'checkout';

export interface Step {
  step: PlanSelectNextStep;
  stepNo: number;
}

/**
 * Util function that returns a doubly linked list depending in the upsellType passed
 */
export const getUpsellSteps = (upsellType: ProductUpsellTypeEnum): LinkedList<Step> => {
  const steps = new LinkedList<Step>();

  switch (upsellType) {
    // Single ask step
    case ProductUpsellTypeEnum.ask:
      steps.add({
        step: 'ask',
        stepNo: 1,
      });
      steps.add({
        step: 'checkout',
        stepNo: 2,
      });
      return steps;
    // Single observe step
    case ProductUpsellTypeEnum.observe:
      steps.add({
        step: 'observe',
        stepNo: 1,
      });

      steps.add({
        step: 'checkout',
        stepNo: 2,
      });
      return steps;
    // For multi step flow, we create a linked list with two steps, first observe (head) then ask step
    case ProductUpsellTypeEnum.multi:
      steps.add({
        step: 'observe',
        stepNo: 1,
      });
      steps.add({
        step: 'ask',
        stepNo: 2,
      });
      steps.add({
        step: 'checkout',
        stepNo: 3,
      });
      return steps;
    default:
      return steps;
  }
};

export const getPlanSelectModalType = (
  checkedProductIds: SelectedProductTypes,
): ProductUpsellTypeEnum => {
  let upsellType;
  if (checkedProductIds[ProductType.Analytics] && checkedProductIds[ProductType.Feedback]) {
    upsellType = ProductUpsellTypeEnum.multi;
  } else if (checkedProductIds[ProductType.Analytics]) {
    upsellType = ProductUpsellTypeEnum.observe;
  } else {
    upsellType = ProductUpsellTypeEnum.ask;
  }
  return upsellType;
};
